import { Link } from "react-router-dom";
import { LandingHeader, LandingFooter } from "@/components/landing";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";
import { ArrowRight } from "lucide-react";

const QuiSommesNous = () => {
  usePageMeta({
    title: "Qui sommes-nous — B8ild | Logiciel gestion chantier BTP",
    description: "B8ild est né d'un constat : 68% des artisans BTP découvrent leurs pertes trop tard. On a créé l'outil de pilotage financier qui manquait aux pros du bâtiment. Made in France, RGPD.",
    url: "https://b8ild.fr/qui-sommes-nous",
  });

  return (
    <div className="min-h-screen bg-[#0F172A] flex flex-col">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "Qui sommes-nous — B8ild",
        description: "B8ild est un logiciel de pilotage de rentabilité chantier pour artisans et PME du BTP, créé en France.",
        url: "https://b8ild.fr/qui-sommes-nous",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
        inLanguage: "fr",
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Qui sommes-nous", item: "https://b8ild.fr/qui-sommes-nous" },
        ],
      }} />
      <LandingHeader />

      {/* Hero */}
      <section className="pt-32 pb-12 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white">
            On a créé l'outil qu'on aurait voulu avoir sur nos chantiers.
          </h1>
          <p className="mt-4 text-lg text-white/60">
            Pas une usine à gaz. Un tableau de bord. Clair, rapide, fait pour le terrain.
          </p>
        </div>
      </section>

      {/* Contenu principal */}
      <section className="flex-1 max-w-3xl mx-auto px-4 pb-20 w-full">
        <div className="space-y-12">

          {/* Le problème */}
          <div className="bg-[#1E293B] rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">
              Le problème qu'on résout
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              68% des professionnels du BTP découvrent qu'ils ont perdu de l'argent sur un chantier... après l'avoir terminé. Pas parce qu'ils bossent mal. Parce qu'ils n'ont aucun outil pour suivre leurs marges en temps réel.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              Un électricien avec 5 chantiers ouverts ne sait pas lequel lui coûte 200€/jour de trop. Un maçon qui fait 180 000€ de CA ne connaît pas sa marge nette réelle. Il la découvre chez le comptable, 3 mois trop tard.
            </p>
            <p className="text-white/70 leading-relaxed">
              La marge nette moyenne dans le BTP tourne entre 3% et 8%. Sur 200 000€ de CA, ça fait entre 6 000€ et 16 000€ de bénéfice. Chaque chantier déficitaire mange une part énorme de ce résultat. Et sans suivi, on ne le voit pas venir.
            </p>
          </div>

          {/* La mission */}
          <div className="bg-[#1E293B] rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">
              Ce qu'on fait
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              B8ild donne aux artisans et PME du bâtiment un tableau de bord de rentabilité. Pas un ERP à 47 modules. Un écran qui dit : ce chantier-là, il te rapporte 18%. Celui-là, il est à -2%. Bouge-toi.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              L'outil calcule le "jour critique" de chaque chantier. Le jour exact où la marge passe sous zéro si rien ne change. 7 jours avant, 3 jours avant, 1 jour avant : alerte. Pas de surprise en fin de chantier.
            </p>
            <p className="text-white/70 leading-relaxed">
              Import des factures par scan (OCR). Calcul automatique des coûts main d'oeuvre. Suivi trésorerie. Rapports PDF en 2 clics. Le tout pensé pour quelqu'un qui a les mains dans le cambouis, pas devant un écran 8h par jour.
            </p>
          </div>

          {/* Made in France */}
          <div className="bg-[#1E293B] rounded-xl p-6 md:p-8">
            <h2 className="text-xl font-bold text-white mb-4">
              Made in France, données en France
            </h2>
            <p className="text-white/70 leading-relaxed mb-4">
              B8ild est conçu et développé en France. Les serveurs sont hébergés en France. Vos données de chantier, vos factures, vos marges : tout reste sur le territoire français.
            </p>
            <p className="text-white/70 leading-relaxed mb-4">
              Conforme RGPD. Chiffrement en transit et au repos. Vous pouvez exporter ou supprimer vos données à tout moment. Pas de revente de données, pas de tracking publicitaire.
            </p>
            <div className="flex flex-wrap gap-3 mt-4">
              {[
                { emoji: "🇫🇷", text: "Conçu en France" },
                { emoji: "🔒", text: "RGPD" },
                { emoji: "☁️", text: "Hébergé en France" },
              ].map((badge, i) => (
                <div
                  key={i}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.05] text-xs text-white/50"
                >
                  <span>{badge.emoji}</span>
                  <span>{badge.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-r from-[#F59E0B] to-[#D4AF37] text-black font-medium hover:opacity-90 transition-opacity"
            >
              Une question ? Contactez-nous
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <LandingFooter />
    </div>
  );
};

export default QuiSommesNous;
